import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Church, User, ShieldCheck, Sparkles, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { AddressFields, EMPTY_ADDRESS, addressComplete } from "@/components/AddressFields";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-login-button"
      className="flex h-12 w-full items-center justify-center gap-3 rounded-full bg-white text-sm font-bold text-[#1F1F1F] transition-transform active:scale-[0.98]">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Continuar com Google
    </button>
  );
}

const HERO = "https://images.pexels.com/photos/6860473/pexels-photo-6860473.jpeg?auto=compress&w=1200";

function RoleToggle({ role, setRole }) {
  const opt = (key, label, Icon) => (
    <button type="button" onClick={() => setRole(key)} data-testid={`role-${key}`}
      className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold transition-colors ${role === key ? "bg-[hsl(var(--gold))] text-[#0B0F17]" : "bg-white/5 text-muted-foreground hover:text-white"}`}>
      <Icon size={16} /> {label}
    </button>
  );
  return <div className="flex gap-2">{opt("user", "Usuário Comum", User)}{opt("institution", "Instituição", Church)}</div>;
}

const Field = ({ label, ...p }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-white/5" required {...p} />
  </div>
);

function PasswordField({ label, ...p }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="relative">
        <Input className="h-11 rounded-xl bg-white/5 pr-11" required type={show ? "text" : "password"} {...p} />
        <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Ocultar senha" : "Mostrar senha"} data-testid="toggle-password-visibility"
          className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-white/10 hover:text-white transition-colors">
          {show ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </div>
    </div>
  );
}

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("user");
  const [f, setF] = useState({ name: "", email: "", password: "", birth_date: "", leader_name: "", denomination: "evangelica" });
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [forgot, setForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inst = mode === "register" && role === "institution";

  const submit = async (e) => {
    e.preventDefault();
    if (inst && !addressComplete(address)) return toast.error("Preencha o endereço completo com CEP e UF");
    setLoading(true);
    try {
      const u = mode === "login" ? await login(f.email, f.password) : await register({ ...f, role, birth_date: role === "user" ? f.birth_date : null, address: inst ? address : null });
      toast.success(`Bem-vindo(a), ${u.name}!`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden grain">
        <img src={HERO} alt="Louvor jovem" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/60 to-transparent" />
        <div className="absolute bottom-0 p-12 max-w-xl rise">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold backdrop-blur"><ShieldCheck size={14} className="text-emerald-400" /> Curadoria institucional</span>
          <h1 className="mt-5 text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">Comunhão ao vivo, <span className="text-[hsl(var(--gold))]">com segurança</span> de ponta a ponta.</h1>
          <p className="mt-4 text-muted-foreground">Rodas de conversa, estudos e louvor guiados por igrejas e paróquias verificadas.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md rise">
          <Logo className="mb-10" />
          <h2 className="text-3xl font-extrabold tracking-tight">{mode === "login" ? "Entrar" : "Criar conta"}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{mode === "login" ? "Que bom te ver de novo." : "Cadastro único e sem fricção diária."}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && <RoleToggle role={role} setRole={setRole} />}
            {mode === "register" && <Field label={role === "user" ? "Seu nome" : "Nome da igreja / paróquia"} value={f.name} onChange={set("name")} data-testid="register-name-input" />}
            {mode === "register" && role === "institution" && (
              <>
                <Field label="Nome do líder responsável" value={f.leader_name} onChange={set("leader_name")} data-testid="register-leader-input" />
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Tradição</Label>
                  <Select value={f.denomination} onValueChange={(v) => setF({ ...f, denomination: v })}>
                    <SelectTrigger className="h-11 rounded-xl bg-white/5" data-testid="register-denomination-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="evangelica">Evangélica</SelectItem>
                      <SelectItem value="catolica">Católica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 pt-1">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[hsl(var(--gold))]">Endereço da instituição</p>
                  <AddressFields value={address} onChange={setAddress} prefix="register-address" />
                </div>
              </>
            )}
            <Field label="E-mail" type="email" value={f.email} onChange={set("email")} data-testid="auth-email-input" />
            <PasswordField label="Senha" minLength={6} value={f.password} onChange={set("password")} data-testid="auth-password-input" />
            {mode === "login" && (
              <button type="button" onClick={() => setForgot(true)} className="-mt-1 block text-xs font-semibold text-[hsl(var(--gold))] hover:underline" data-testid="forgot-password-link">Esqueceu sua senha?</button>
            )}
            {mode === "register" && role === "user" && <Field label="Data de nascimento" type="date" value={f.birth_date} onChange={set("birth_date")} data-testid="register-birthdate-input" />}
            {mode === "register" && role === "user" && <p className="text-xs text-muted-foreground flex gap-2"><Sparkles size={14} className="text-[hsl(var(--gold))] shrink-0" /> Menores de 18 aceitam um termo parental simples apenas no primeiro acesso.</p>}
            <Button type="submit" disabled={loading} className="h-12 w-full rounded-full text-base font-bold" data-testid="auth-submit-button">
              {loading ? "Aguarde…" : mode === "login" ? "Entrar" : "Criar minha conta"}
            </Button>
          </form>
          {!inst && (
            <div className="mt-5 space-y-3" data-testid="google-login-section">
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-muted-foreground"><span className="h-px flex-1 bg-white/10" />ou<span className="h-px flex-1 bg-white/10" /></div>
              <GoogleButton />
              <p className="text-center text-[11px] text-muted-foreground">Para usuários comuns. Instituições entram com e-mail e senha.</p>
            </div>
          )}
          <ForgotPasswordDialog open={forgot} onOpenChange={setForgot} initialEmail={f.email} />
          <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-6 text-sm text-muted-foreground hover:text-white transition-colors" data-testid="auth-toggle-mode">
            {mode === "login" ? "Ainda não tem conta? " : "Já tem conta? "}
            <span className="font-semibold text-[hsl(var(--gold))]">{mode === "login" ? "Cadastre-se" : "Entrar"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
